/**
 * chunkReload — auto-recover from stale-chunk errors after a mid-session deploy.
 *
 * When the bundle is rebuilt and the user's tab still has the OLD index.html in
 * memory, any lazy `import(...)` for a page whose chunk hash changed will throw
 * "Failed to fetch dynamically imported module" (or similar). The fix is to
 * force a single hard reload, which pulls the fresh index.html with correct
 * chunk refs. We guard with sessionStorage so a genuine non-deploy error
 * (missing asset, CORS, network down) doesn't loop the tab forever.
 *
 * Added Apr 24, 2026 — frontend-only. No backend touch, no new dep.
 */

const CHUNK_ERROR_PATTERN =
  /(Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed|error loading dynamically imported module)/i;

const RELOAD_KEY = 'aura-chunk-reload-done';

/**
 * If the given error looks like a stale-chunk / dynamic-import failure AND
 * this tab hasn't already tried a reload in this session, set the flag and
 * hard-reload. Returns true if a reload was triggered (caller should stop
 * further handling).
 */
export function tryAutoReload(error: unknown): boolean {
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String((error as { message?: unknown })?.message ?? error ?? '');
  if (!CHUNK_ERROR_PATTERN.test(msg)) return false;

  try {
    if (sessionStorage.getItem(RELOAD_KEY)) return false; // already retried; give up
    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
  } catch {
    // sessionStorage may be unavailable (private mode, strict settings) — fall
    // through to reload anyway; worst case is one extra reload attempt.
  }

  // eslint-disable-next-line no-console
  console.warn('[AURA] Stale chunk detected — auto-reloading to pick up fresh bundle.');
  window.location.reload();
  return true;
}

/**
 * Install global listeners that catch script-load errors bypassing React, and
 * clear the one-shot flag once the app has successfully mounted for a few
 * seconds (so the NEXT future deploy can retry again).
 */
export function installChunkReloadHandlers(): void {
  window.addEventListener('error', (e) => {
    if (tryAutoReload(e.error ?? e.message)) {
      e.preventDefault();
    }
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (tryAutoReload(e.reason)) {
      e.preventDefault();
    }
  });

  // Clear the retry flag after the app has been alive for a few seconds —
  // this means future deploys during the same tab session can also auto-heal.
  setTimeout(() => {
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      /* ignore */
    }
  }, 5000);
}
